// Static option lists for the (frontend-only) event creation flow.
// Curated for the app's current US / Colorado focus; easy to swap for an API later.

export const EVENT_FORMATS = [
  { value: 'in_person', label: 'In person' },
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
];

export const EVENT_CATEGORIES = [
  'Networking',
  'Webinar',
  'Workshop',
  'Seminar',
  'Conference',
  'Social',
  'Fundraiser',
  'Other',
];

export const EVENT_CITIES = [
  'Denver, CO',
  'Boulder, CO',
  'Colorado Springs, CO',
  'Fort Collins, CO',
  'Aurora, CO',
  'Lakewood, CO',
];

export const EVENT_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
];

export const formatLabel = (value) =>
  EVENT_FORMATS.find((f) => f.value === value)?.label || value;

export const isLocationFormat = (format) => format === 'in_person' || format === 'hybrid';
export const isOnlineFormat = (format) => format === 'online' || format === 'hybrid';

// ─────────────────────────────────────────────────────────────────────────────
// Compliance disclosures — single source of truth for the questionnaire step.
//
// This drives BOTH the wizard's capture UI and the reviewer's read-only view,
// so the question wording an advisor attests to is byte-for-byte the wording a
// reviewer (and any audit) reads back. The persisted shape mirrors this:
//   complianceDisclosures: { [block.key]: { answer, payer, amount } }
// Keep the keys in lockstep with backend `ComplianceDisclosures`.
// ─────────────────────────────────────────────────────────────────────────────

export const SOCIAL_NETWORKS = [
  { value: 'x', label: 'X' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'linkedin', label: 'LinkedIn' },
];

export const DISCLOSURE_YES = 'yes';
export const DISCLOSURE_NO = 'no';

export const DISCLOSURE_ANSWER_OPTIONS = [
  { value: DISCLOSURE_YES, label: 'Yes' },
  { value: DISCLOSURE_NO, label: 'No' },
];

export const EVENT_DISCLOSURES = [
  {
    key: 'sponsorship',
    gateLabel: 'Is this event sponsored by a third party?',
    gateHint:
      'A sponsor is any outside firm or person contributing funds, venue, or goods toward this event.',
    payerLabel: 'Who is the sponsor?',
    payerPlaceholder: 'e.g. Acme Asset Management',
    amountLabel: 'Total sponsorship amount',
    amountPlaceholder: 'e.g. 5,000',
  },
  {
    key: 'firmPayment',
    gateLabel: 'Are you or your firm paying for this event?',
    gateHint:
      'Covers any cost your firm or you personally bear — venue, catering, materials, or staff time billed to the event.',
    payerLabel: 'Who at your firm is covering the cost?',
    payerPlaceholder: 'e.g. Jordan Lee, Marketing',
    amountLabel: 'Total amount your firm is paying',
    amountPlaceholder: 'e.g. 3,200',
  },
  {
    key: 'alcohol',
    gateLabel: 'Will alcohol be served at this event?',
    gateHint:
      'Disclose alcohol regardless of who pays — it carries its own entertainment and conduct considerations.',
    payerLabel: 'Who is paying for the alcohol?',
    payerPlaceholder: 'e.g. The venue / Acme Asset Management',
    noAmount: true,
  },
];

// An empty, fully-shaped disclosures object. Used to seed the wizard form and
// to safely hydrate when editing an event saved before this field existed.
export const emptyDisclosures = () =>
  EVENT_DISCLOSURES.reduce((acc, block) => {
    acc[block.key] = { answer: '', payer: '', amount: '' };
    return acc;
  }, {});

// ─────────────────────────────────────────────────────────────────────────────
// Regulatory disclosure — appended to every event description visible to
// attendees and compliance reviewers. Satisfies SEC Rule 206(4)-3 (Solicitor
// Disclosure) and the SEC Marketing Rule 206(4)-1 requirements for paid
// promoters: solicitor identity, compensation structure, conflict of interest,
// no extra charge to the attendee, and link to full written disclosures.
// ─────────────────────────────────────────────────────────────────────────────
export const GILLYREACH_DISCLOSURE = `This event is marketed by GillyReach, Inc., a paid third-party promoter. GillyReach receives compensation from the presenting advisor for marketing and referral services.`
